













































import IExternalStorageApiModel from '@/model/api/externalStorage/IExternalStorageApiModel';
import container from '@/model/ModelContainer';
import ISnackbarState from '@/model/state/snackbar/ISnackbarState';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as apid from '../../../../api';

@Component
export default class MoveToExternalDialog extends Vue {
    @Prop({ required: true })
    public isOpen!: boolean;

    // 単品用 (既存の呼び出し互換)
    @Prop({ required: false, default: null })
    public recordedItem!: apid.RecordedItem | null;

    // 複数一括用
    @Prop({ required: false, default: (): apid.RecordedItem[] => [] })
    public recordedItems!: apid.RecordedItem[];

    public storageOptions: { name: string; label: string; path: string }[] = [];
    public selectedStorageName: string | null = null;
    public subDirectory: string = '';
    public isSubmitting: boolean = false;
    public progressCurrent: number = 0;
    public history: apid.ExternalStorageMoveHistoryItem[] = [];
    public directories: string[] = [];
    public isLoadingDirectories: boolean = false;

    /**
     * サブディレクトリ候補。過去の移動先 (履歴) を優先で先頭に並べ、
     * その後に選択中ストレージ内の実フォルダ (2階層) を重複排除して続ける。
     */
    get subDirectorySuggestions(): string[] {
        if (this.selectedStorageName === null) return [];
        const seen = new Set<string>();
        const out: string[] = [];
        for (const h of this.history) {
            if (h.storageName !== this.selectedStorageName) continue;
            if (h.subDirectory.length === 0) continue;
            if (seen.has(h.subDirectory)) continue;
            seen.add(h.subDirectory);
            out.push(h.subDirectory);
        }
        for (const d of this.directories) {
            if (d.length === 0) continue;
            if (seen.has(d)) continue;
            seen.add(d);
            out.push(d);
        }
        return out;
    }

    private externalStorageApi = container.get<IExternalStorageApiModel>('IExternalStorageApiModel');
    private snackbar = container.get<ISnackbarState>('ISnackbarState');

    get dialogModel(): boolean {
        return this.isOpen;
    }
    set dialogModel(value: boolean) {
        this.$emit('update:isOpen', value);
    }

    /**
     * 実際に移動対象にする RecordedItem 一覧 (録画中/エンコード中/既に外部はスキップ)
     */
    get targetItems(): apid.RecordedItem[] {
        const all: apid.RecordedItem[] = this.recordedItems.length > 0 ? this.recordedItems : this.recordedItem ? [this.recordedItem] : [];
        return all.filter(r => r.isRecording === false && r.isEncoding === false && typeof r.externalPath === 'undefined');
    }

    get skippedCount(): number {
        const all = this.recordedItems.length > 0 ? this.recordedItems : this.recordedItem ? [this.recordedItem] : [];
        return all.length - this.targetItems.length;
    }

    get progressPercent(): number {
        if (this.targetItems.length === 0) return 0;
        return Math.round((this.progressCurrent / this.targetItems.length) * 100);
    }

    get canSubmit(): boolean {
        return typeof this.selectedStorageName === 'string' && this.selectedStorageName.length > 0 && this.targetItems.length > 0;
    }

    @Watch('isOpen')
    public async onOpen(newVal: boolean): Promise<void> {
        if (newVal === true) {
            this.directories = [];
            await Promise.all([this.loadStorages(), this.loadHistory()]);
            this.subDirectory = '';
            this.progressCurrent = 0;
            if (this.storageOptions.length === 1 && this.selectedStorageName === null) {
                this.selectedStorageName = this.storageOptions[0].name;
            }
            if (this.selectedStorageName !== null) {
                await this.loadDirectories(this.selectedStorageName);
            }
        }
    }

    @Watch('selectedStorageName')
    public async onStorageChange(newVal: string | null): Promise<void> {
        // ダイアログを開いていない間の変化 (リセット等) は無視
        if (this.isOpen === false || newVal === null) {
            this.directories = [];
            return;
        }
        await this.loadDirectories(newVal);
    }

    public async loadDirectories(storageName: string): Promise<void> {
        this.isLoadingDirectories = true;
        try {
            const res = await this.externalStorageApi.getDirectories(storageName, 2);
            // 取得中にストレージが切り替わっていたら破棄
            if (this.selectedStorageName === storageName) {
                this.directories = res.items;
            }
        } catch (err) {
            this.directories = [];
        } finally {
            this.isLoadingDirectories = false;
        }
    }

    public async loadStorages(): Promise<void> {
        try {
            const list = await this.externalStorageApi.getList();
            this.storageOptions = list.items.map(item => ({
                name: item.name,
                label: `${item.name} (${item.path})`,
                path: item.path,
            }));
        } catch (err) {
            this.snackbar.open({ color: 'error', text: '外部ストレージ一覧の取得に失敗' });
        }
    }

    public async loadHistory(): Promise<void> {
        try {
            const h = await this.externalStorageApi.getHistory(10);
            this.history = h.items;
        } catch (err) {
            this.history = [];
        }
    }

    public async submit(): Promise<void> {
        if (!this.canSubmit || this.selectedStorageName === null) {
            return;
        }
        this.isSubmitting = true;
        this.progressCurrent = 0;

        const storageName = this.selectedStorageName;
        const subDirectory = this.subDirectory.length > 0 ? this.subDirectory : null;
        const ids = this.targetItems.map(i => i.id);

        try {
            const job = await this.externalStorageApi.submitMoveJob({
                recordedIds: ids,
                storageName,
                subDirectory,
            });
            this.snackbar.open({
                color: 'success',
                text: ids.length === 1 ? '移動ジョブを登録しました' : `移動ジョブを登録しました (${ids.length} 件)`,
            });
            this.dialogModel = false;
            this.$emit('moved', job.id);
        } catch (err: any) {
            this.snackbar.open({
                color: 'error',
                text: `ジョブ登録失敗: ${err?.response?.data?.message ?? err.message ?? ''}`,
            });
        } finally {
            this.isSubmitting = false;
        }
    }
}
